import React, { useEffect, useRef, useState } from 'react';

export const GravityRunner: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [highScore, setHighScore] = useState(parseInt(localStorage.getItem('gp_highscore') || '0'));

    const resetGameRef = useRef<() => void>(() => { });
    const gameStateRef = useRef({ isRunning: true, gameOver: false });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Game State
        let animationFrameId: number;
        let gameScore = 0;
        let speed = 5;

        let gravity = 0.6;
        let lift = -12; // Jump force not used, we flip gravity

        // Player
        const player = {
            x: 50,
            y: canvas.height / 2,
            width: 30,
            height: 30,
            dy: 0,
            onGround: false,
            inverted: false, // Gravity direction
            color: '#8b5cf6' // Violet-500
        };

        // Obstacles
        let obstacles: { x: number, y: number, width: number, height: number, type: 'ground' | 'ceiling' }[] = [];
        let particles: { x: number, y: number, size: number, color: string, speedX: number, speedY: number, life: number }[] = [];

        const resetGame = () => {
            gameScore = 0;
            setScore(0);
            speed = 5;
            obstacles = [];
            particles = [];
            player.y = canvas.height / 2;
            player.dy = 0;
            player.inverted = false;

            gameStateRef.current.isRunning = true;
            gameStateRef.current.gameOver = false;
            setGameOver(false);

            loop();
        };
        resetGameRef.current = resetGame;

        const createExplosion = (x: number, y: number, color: string) => {
            for (let i = 0; i < 20; i++) {
                particles.push({
                    x, y,
                    size: Math.random() * 4 + 1,
                    color,
                    speedX: (Math.random() - 0.5) * 10,
                    speedY: (Math.random() - 0.5) * 10,
                    life: 1.0
                });
            }
        };

        const drawPlayer = () => {
            ctx.fillStyle = player.color;
            ctx.shadowBlur = 20;
            ctx.shadowColor = player.color;
            ctx.fillRect(player.x, player.y, player.width, player.height);

            // Ears (Simple Dog shape)
            ctx.beginPath();
            if (!player.inverted) {
                ctx.moveTo(player.x, player.y);
                ctx.lineTo(player.x + 5, player.y - 10);
                ctx.lineTo(player.x + 10, player.y);
                ctx.moveTo(player.x + 20, player.y);
                ctx.lineTo(player.x + 25, player.y - 10);
                ctx.lineTo(player.x + 30, player.y);
            } else {
                ctx.moveTo(player.x, player.y + player.height);
                ctx.lineTo(player.x + 5, player.y + player.height + 10);
                ctx.lineTo(player.x + 10, player.y + player.height);
                ctx.moveTo(player.x + 20, player.y + player.height);
                ctx.lineTo(player.x + 25, player.y + player.height + 10);
                ctx.lineTo(player.x + 30, player.y + player.height);
            }
            ctx.fill();
            ctx.shadowBlur = 0;

            // Trail
            if (Math.random() > 0.5) {
                particles.push({
                    x: player.x,
                    y: player.y + player.height / 2,
                    size: Math.random() * 3,
                    color: player.color,
                    speedX: -speed,
                    speedY: (Math.random() - 0.5) * 2,
                    life: 0.5
                });
            }
        };

        const update = () => {
            if (!gameStateRef.current.isRunning) return;

            // Gravity Logic
            if (player.inverted) {
                player.dy -= gravity; // Fall Up
            } else {
                player.dy += gravity; // Fall Down
            }
            player.y += player.dy;

            // Boundaries
            if (player.y < 0) {
                player.y = 0;
                player.dy = 0;
                player.onGround = true; // Ceiling is ground when inverted
            }
            if (player.y + player.height > canvas.height) {
                player.y = canvas.height - player.height;
                player.dy = 0;
                player.onGround = true;
            }

            // Obstacle Spawner
            if (Math.random() < 0.02 + (gameScore * 0.0001)) { // Difficulty increases
                const height = Math.random() * (canvas.height / 3) + 40;
                const isCeiling = Math.random() > 0.5;
                obstacles.push({
                    x: canvas.width,
                    y: isCeiling ? 0 : canvas.height - height,
                    width: 40,
                    height: height,
                    type: isCeiling ? 'ceiling' : 'ground'
                });
            }

            // Update Obstacles
            obstacles.forEach((obs, index) => {
                obs.x -= speed;

                // Collision
                if (
                    player.x < obs.x + obs.width &&
                    player.x + player.width > obs.x &&
                    player.y < obs.y + obs.height &&
                    player.y + player.height > obs.y
                ) {
                    // Auto-Play? No, die.
                    gameOverFunc();
                }

                // Remove off-screen
                if (obs.x + obs.width < 0) {
                    obstacles.splice(index, 1);
                    gameScore++;
                    setScore(gameScore);
                    if (gameScore % 10 === 0) speed += 0.5;
                }
            });

            // Particles
            particles.forEach((p, i) => {
                p.x += p.speedX;
                p.y += p.speedY;
                p.life -= 0.02;
                if (p.life <= 0) particles.splice(i, 1);
            });
        };

        const draw = () => {
            // Background
            ctx.fillStyle = '#0f172a'; // slate-900
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Grid lines for retro feel
            ctx.strokeStyle = '#1e293b';
            ctx.lineWidth = 1;
            for (let i = 0; i < canvas.width; i += 40) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, canvas.height); ctx.stroke(); }
            for (let i = 0; i < canvas.height; i += 40) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(canvas.width, i); ctx.stroke(); }

            drawPlayer();

            // Obstacles
            ctx.fillStyle = '#ef4444'; // Red-500
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#ef4444';
            obstacles.forEach(obs => {
                ctx.fillRect(obs.x, obs.y, obs.width, obs.height);
            });
            ctx.shadowBlur = 0;

            // Particles
            particles.forEach(p => {
                ctx.fillStyle = p.color;
                ctx.globalAlpha = p.life;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = 1;
            });

            // Floor/Ceiling lines
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, 2);
            ctx.fillRect(0, canvas.height - 2, canvas.width, 2);
        };

        const loop = () => {
            update();
            draw();
            if (gameStateRef.current.isRunning) {
                animationFrameId = requestAnimationFrame(loop);
            }
        };

        const gameOverFunc = () => {
            gameStateRef.current.isRunning = false;
            gameStateRef.current.gameOver = true;
            setGameOver(true);

            createExplosion(player.x, player.y, player.color);
            draw(); // Draw one last time to show explosion roughly

            const currentHigh = parseInt(localStorage.getItem('gp_highscore') || '0');
            if (gameScore > currentHigh) {
                localStorage.setItem('gp_highscore', gameScore.toString());
                setHighScore(gameScore);
            }
        };

        // Inputs
        const handleInput = () => {
            // If game over, wait for manual restart via button (or we could enable tap to restart)
            if (gameStateRef.current.isRunning) {
                player.inverted = !player.inverted;
            }
        };

        // Resize
        const handleResize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            resetGame();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('mousedown', handleInput);
        window.addEventListener('touchstart', handleInput);
        window.addEventListener('keydown', (e) => {
            if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'ArrowDown') handleInput();
        });

        handleResize(); // Init size

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousedown', handleInput);
            window.removeEventListener('touchstart', handleInput);
            cancelAnimationFrame(animationFrameId);
            gameStateRef.current.isRunning = false;
        };
    }, []);

    return (
        <div className="relative w-full h-full bg-slate-900 overflow-hidden">
            <canvas ref={canvasRef} className="block w-full h-full touch-none" />

            {/* HUD */}
            <div className="absolute top-4 right-4 text-right pointer-events-none select-none">
                <div className="text-4xl font-black font-mono text-white drop-shadow-lg">{score.toString().padStart(5, '0')}</div>
                <div className="text-xs font-bold text-slate-500 uppercase">HI: {highScore.toString().padStart(5, '0')}</div>
            </div>

            {/* Game Over Screen */}
            {gameOver && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-50 animate-in zoom-in duration-300">
                    <div className="text-center p-8 bg-slate-800 rounded-3xl border border-slate-700 shadow-2xl">
                        <h2 className="text-4xl font-black text-rose-500 mb-2 uppercase italic">¡Te estrellaste!</h2>
                        <p className="text-slate-400 mb-6">Tu mascota tropezó.</p>
                        <div className="text-6xl font-mono font-black text-white mb-8">{score}</div>
                        <button
                            className="bg-violet-600 hover:bg-violet-500 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest text-lg shadow-lg shadow-violet-600/30 animate-pulse"
                            onClick={() => resetGameRef.current()}
                        >
                            Jugar de Nuevo
                        </button>
                    </div>
                </div>
            )}

            {!gameOver && score === 0 && (
                <div className="absolute bottom-20 left-0 w-full text-center opacity-50 pointer-events-none animate-bounce">
                    <p className="text-sm font-bold uppercase tracking-widest text-white">Toca la pantalla para invertir gravedad</p>
                </div>
            )}
        </div>
    );
};
